import { api } from "./client";
import type { ApiResponse } from "./types";

/**
 * Content reports — the first moderation surface.
 *
 * One write anyone can make, two admin verbs. The design is "no queue, no
 * SLA, but when someone DOES look, everything is there, loudest item first" —
 * so the admin list arrives grouped by content item and sorted by report
 * count, and the client renders that order as-is.
 */

/** What can be reported. Closed — the backend validates against this list. */
export const REPORTABLE_TYPES = [
  "voice",
  "question",
  "voice_story",
  "answer",
  "voice_comment",
] as const;
export type ReportableType = (typeof REPORTABLE_TYPES)[number]
  | "people_name";

export const REPORT_REASONS = [
  "harassment",
  "hate_speech",
  "spam",
  "misinformation",
  "self_harm_concern",
  "impersonation",
  "other",
] as const;
export type ReportReason = (typeof REPORT_REASONS)[number]
  | "not_how_i_say_it";

/** Human labels. `self_harm_concern` is worry FOR someone, not about content. */
export const REASON_LABELS: Record<ReportReason, string> = {
  harassment: "Harassment or bullying",
  hate_speech: "Hate speech",
  spam: "Spam or a scam",
  misinformation: "False or misleading",
  self_harm_concern: "I'm worried about this person",
  impersonation: "Pretending to be someone else",
  other: "Something else",
  // A people-name only: the word is not what people of that community say.
  not_how_i_say_it: "This is not how I'd say it",
};

export interface ReportItem {
  id: string;
  contentType: ReportableType;
  contentId: string;
  reason: ReportReason;
  details: string | null;
  status: string;
  createdAt: string;
  reporter: { id: string; name: string };
}

/** One reported content item, with everyone who reported it. */
export interface ReportGroup {
  contentType: ReportableType;
  contentId: string;
  reportCount: number;
  reports: ReportItem[];
}

/**
 * Report a piece of content. Always 201 — the response deliberately does not
 * reveal whether others already reported the same item, and re-reporting
 * updates your report rather than stacking a second one.
 */
export function createReport(input: {
  contentType: ReportableType;
  contentId: string;
  reason: ReportReason;
  details?: string;
}) {
  return api.post<ApiResponse<{ id: string }>>("/reports", input);
}

/** Admin: the queue, loudest first. Default status filter is `open`. */
/**
 * The moderation list leaves people-name reports out; they are for the
 * people-names list's owner and are read only by asking for the type.
 */
export function listAdminReports(params?: { status?: string; limit?: number; contentType?: ReportableType }) {
  return api.get<ApiResponse<{ items: ReportGroup[] }>>(
    "/reports/admin",
    params as Record<string, string | number | boolean | undefined>,
  );
}

/**
 * Admin: resolve every open report on one content item at once. `reviewed`
 * means a human looked and acted (or chose not to); `dismissed` means the
 * reports were unfounded. Neither deletes content — that stays a separate,
 * deliberate act.
 */
export function resolveReports(input: {
  contentType: ReportableType;
  contentId: string;
  resolution: "reviewed" | "dismissed";
}) {
  return api.post<ApiResponse<{ resolved: number }>>("/reports/admin/resolve", input);
}
